import React, { useState, useCallback, useRef } from 'react'
import { X, Download, Upload, FileSpreadsheet, AlertTriangle, CheckCircle } from 'lucide-react'
import axios from 'axios'
import Button from '@/components/ui/Button'
import { designTokens } from '@/utils/design-tokens'
import { useAppSelector } from '@/store/hooks'
import { useAlert } from '@/components/ui/Alert/exports'
import { OpenAPI } from '@/api'

interface PerformanceBulkCreateModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

type ModalStep = 'template' | 'upload' | 'result'

interface UploadResult {
  success: boolean
  createdCount?: number
  message?: string
}

const PerformanceBulkCreateModal: React.FC<PerformanceBulkCreateModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)
  const { showSuccess, showError } = useAlert()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [step, setStep] = useState<ModalStep>('template')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const resetModal = useCallback(() => {
    setStep('template')
    setSelectedFile(null)
    setIsDownloadingTemplate(false)
    setIsUploading(false)
    setUploadResult(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const handleClose = useCallback(() => {
    resetModal()
    onClose()
  }, [resetModal, onClose])

  const handleDownloadTemplate = useCallback(async () => {
    if (!selectedWorkspace?.id || isDownloadingTemplate) return

    setIsDownloadingTemplate(true)

    try {
      const response = await axios.get(
        `${OpenAPI.BASE}/api/performance/excel/template`,
        {
          responseType: 'blob',
          withCredentials: true,
          headers: {
            ...(OpenAPI.TOKEN && { 'Authorization': `Bearer ${OpenAPI.TOKEN}` }),
          },
          params: { workspace_id: selectedWorkspace.id },
        }
      )

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = '성과지표_일괄생성_템플릿.xlsx'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      showSuccess('템플릿 다운로드가 완료되었습니다.')
      setStep('upload')
    } catch (error: any) {
      console.error('템플릿 다운로드 실패:', error)
      showError('템플릿 다운로드에 실패했습니다.')
    } finally {
      setIsDownloadingTemplate(false)
    }
  }, [selectedWorkspace, isDownloadingTemplate, showSuccess, showError])

  const validateAndSetFile = useCallback((file: File) => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ]
    if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      showError('엑셀 파일(.xlsx, .xls)만 업로드할 수 있습니다.')
      return
    }
    setSelectedFile(file)
  }, [showError])

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) validateAndSetFile(file)
  }, [validateAndSetFile])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) validateAndSetFile(file)
  }, [validateAndSetFile])

  const handleUpload = useCallback(async () => {
    if (!selectedFile || !selectedWorkspace?.id || isUploading) return

    setIsUploading(true)

    try {
      const formData = new FormData()
      formData.append('file', selectedFile)

      const response = await axios.post(
        `${OpenAPI.BASE}/api/performance/excel/upload`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(OpenAPI.TOKEN && { 'Authorization': `Bearer ${OpenAPI.TOKEN}` }),
          },
          params: { workspace_id: selectedWorkspace.id },
          responseType: 'blob',
        }
      )

      const contentType = response.headers['content-type'] || ''

      if (contentType.includes('application/json')) {
        const text = await response.data.text()
        const result = JSON.parse(text)
        setUploadResult({
          success: true,
          createdCount: result.created_count ?? result.updated_count ?? 0,
          message: result.message,
        })
        setStep('result')
        onSuccess()
      } else if (contentType.includes('spreadsheet') || contentType.includes('octet-stream')) {
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = '성과지표_일괄생성_오류목록.xlsx'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        setUploadResult({
          success: false,
          message: '데이터 검증에 실패한 항목이 있습니다. 오류 목록 엑셀 파일을 확인해주세요.',
        })
        setStep('result')
      } else {
        const text = await response.data.text()
        try {
          const result = JSON.parse(text)
          setUploadResult({
            success: true,
            createdCount: result.created_count ?? result.updated_count ?? 0,
            message: result.message,
          })
        } catch {
          setUploadResult({ success: true, message: text })
        }
        setStep('result')
        onSuccess()
      }
    } catch (error: any) {
      console.error('엑셀 업로드 실패:', error)

      if (error?.response?.data instanceof Blob) {
        const contentType = error.response.headers['content-type'] || ''
        if (contentType.includes('spreadsheet') || contentType.includes('octet-stream')) {
          const blob = new Blob([error.response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = '성과지표_일괄생성_오류목록.xlsx'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)

          setUploadResult({
            success: false,
            message: '데이터 검증에 실패한 항목이 있습니다. 오류 목록 엑셀 파일을 확인해주세요.',
          })
          setStep('result')
          return
        }

        try {
          const text = await error.response.data.text()
          const errData = JSON.parse(text)
          setUploadResult({
            success: false,
            message: errData.detail || errData.message || '업로드에 실패했습니다.',
          })
          setStep('result')
          return
        } catch {
          // fall through
        }
      }

      showError('엑셀 업로드에 실패했습니다.')
    } finally {
      setIsUploading(false)
    }
  }, [selectedFile, selectedWorkspace, isUploading, onSuccess, showError])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div
        className="relative bg-white rounded-lg shadow-xl w-full max-w-[520px] mx-4"
        style={{ fontFamily: designTokens.typography.fontFamily.sans[0] }}
      >
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderBottomColor: designTokens.colors.gray[1] }}
        >
          <h2
            style={{
              fontSize: designTokens.typography.fontSize['title-small'][0],
              fontWeight: 700,
              color: designTokens.colors.gray[6],
            }}
          >
            성과지표 일괄 생성
          </h2>
          <button onClick={handleClose} className="p-1 rounded hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5" style={{ color: designTokens.colors.gray[4] }} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {step === 'template' && (
            <>
              <div
                className="flex items-start gap-3 p-4 rounded-lg border"
                style={{ borderColor: designTokens.colors.gray[1] }}
              >
                <div
                  className="p-2 rounded"
                  style={{ backgroundColor: designTokens.colors.gray[0], color: designTokens.colors.primary.DEFAULT }}
                >
                  <FileSpreadsheet className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <p style={{ fontSize: designTokens.typography.fontSize['body-medium'][0], fontWeight: 600, color: designTokens.colors.gray[6] }}>
                    1단계: 템플릿 다운로드
                  </p>
                  <p className="mt-1" style={{ fontSize: designTokens.typography.fontSize['body-small'][0], color: designTokens.colors.gray[5], lineHeight: '1.5' }}>
                    엑셀 템플릿을 다운로드하여 성과지표 데이터를 입력해주세요.
                  </p>
                </div>
              </div>
              <div
                className="p-3 rounded"
                style={{ backgroundColor: designTokens.colors.gray[0], fontSize: designTokens.typography.fontSize['body-small'][0], color: designTokens.colors.gray[5], lineHeight: '1.6' }}
              >
                템플릿의 안내 시트를 참고하여 데이터를 입력한 후, 다음 단계에서 업로드하세요.
              </div>
            </>
          )}

          {step === 'upload' && (
            <>
              <div
                className="flex items-start gap-3 p-4 rounded-lg border"
                style={{ borderColor: designTokens.colors.gray[1] }}
              >
                <div
                  className="p-2 rounded"
                  style={{ backgroundColor: designTokens.colors.gray[0], color: designTokens.colors.primary.DEFAULT }}
                >
                  <Upload className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <p style={{ fontSize: designTokens.typography.fontSize['body-medium'][0], fontWeight: 600, color: designTokens.colors.gray[6] }}>
                    2단계: 엑셀 업로드
                  </p>
                  <p className="mt-1" style={{ fontSize: designTokens.typography.fontSize['body-small'][0], color: designTokens.colors.gray[5], lineHeight: '1.5' }}>
                    작성한 엑셀 파일을 선택해주세요.
                  </p>
                </div>
              </div>

              <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
                <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFileChange} className="hidden" id="perf-excel-upload" />
                <label
                  htmlFor="perf-excel-upload"
                  className="flex flex-col items-center justify-center gap-2 px-4 py-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors"
                  style={{
                    borderColor: isDragging ? designTokens.colors.primary.DEFAULT : designTokens.colors.gray[2],
                    backgroundColor: isDragging ? designTokens.colors.gray[0] : 'transparent',
                  }}
                >
                  {selectedFile ? (
                    <div className="flex items-center gap-2">
                      <FileSpreadsheet className="w-5 h-5" style={{ color: designTokens.colors.primary.DEFAULT }} />
                      <span style={{ fontSize: designTokens.typography.fontSize['body-medium'][0], color: designTokens.colors.gray[6] }}>{selectedFile.name}</span>
                      <span style={{ fontSize: designTokens.typography.fontSize['body-small'][0], color: designTokens.colors.gray[4] }}>({(selectedFile.size / 1024).toFixed(1)} KB)</span>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8" style={{ color: designTokens.colors.gray[3] }} />
                      <span style={{ fontSize: designTokens.typography.fontSize['body-medium'][0], color: designTokens.colors.gray[4] }}>
                        {isDragging ? '여기에 파일을 놓으세요' : '파일을 드래그하거나 클릭하여 선택'}
                      </span>
                      <span style={{ fontSize: designTokens.typography.fontSize['body-xsmall'][0], color: designTokens.colors.gray[3] }}>.xlsx, .xls 파일만 가능</span>
                    </>
                  )}
                </label>
              </div>

              <button type="button" onClick={() => setStep('template')} className="text-sm hover:underline" style={{ color: designTokens.colors.gray[4] }}>
                ← 템플릿 다시 다운로드
              </button>
            </>
          )}

          {step === 'result' && uploadResult && (
            <div className="flex items-start gap-3 p-4 rounded-lg" style={{ backgroundColor: uploadResult.success ? '#E8F5E8' : '#FFF3F0' }}>
              {uploadResult.success ? (
                <CheckCircle className="w-6 h-6 flex-shrink-0" style={{ color: designTokens.colors.success.DEFAULT }} />
              ) : (
                <AlertTriangle className="w-6 h-6 flex-shrink-0" style={{ color: designTokens.colors.error.DEFAULT }} />
              )}
              <div>
                <p style={{ fontSize: designTokens.typography.fontSize['body-medium'][0], fontWeight: 600, color: designTokens.colors.gray[6] }}>
                  {uploadResult.success ? '일괄 생성 완료' : '일괄 생성 실패'}
                </p>
                <p className="mt-1" style={{ fontSize: designTokens.typography.fontSize['body-small'][0], color: designTokens.colors.gray[5], lineHeight: '1.5' }}>
                  {uploadResult.success && uploadResult.createdCount !== undefined
                    ? `${uploadResult.createdCount}건의 성과지표가 생성되었습니다.`
                    : uploadResult.message}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t" style={{ borderTopColor: designTokens.colors.gray[1] }}>
          {step === 'template' && (
            <>
              <Button size="medium" color="white" onClick={handleDownloadTemplate} disabled={isDownloadingTemplate || !selectedWorkspace?.id} className="flex items-center gap-1.5">
                <Download className="w-4 h-4" />
                {isDownloadingTemplate ? '다운로드 중...' : '템플릿 다운로드'}
              </Button>
              <div className="flex-1" />
              <Button size="medium" color="white" onClick={handleClose}>취소</Button>
              <Button size="medium" color="dark" onClick={() => setStep('upload')}>다음</Button>
            </>
          )}
          {step === 'upload' && (
            <>
              <Button size="medium" color="white" onClick={handleClose} disabled={isUploading}>취소</Button>
              <Button size="medium" color="dark" onClick={handleUpload} disabled={!selectedFile || isUploading} className="flex items-center gap-1.5">
                <Upload className="w-4 h-4" />
                {isUploading ? '업로드 중...' : '업로드'}
              </Button>
            </>
          )}
          {step === 'result' && (
            <Button size="medium" color="dark" onClick={handleClose}>확인</Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default PerformanceBulkCreateModal
